import { useState } from 'react';
import { useApp } from '../context/AppContext.jsx';
import { usePersistentState } from '../hooks/usePersistentState.js';
import { api } from '../lib/api.js';
import { AUDIT_COUNTS_KEY, AUDIT_NAME_KEY } from '../lib/constants.js';
import { Btn, Field, MonoInput, PageHeading } from '../components/ui.jsx';

export default function InventoryAudit() {
  const { snapshot, loading, pushToast, refresh } = useApp();
  const items = snapshot.items || [];

  // Physical counts + name are cached so refreshing, switching tabs, or
  // navigating away and back restores a half-finished audit.
  const [name, setName] = usePersistentState(AUDIT_NAME_KEY, '');
  const [counts, setCounts] = usePersistentState(AUDIT_COUNTS_KEY, {});
  const [submitting, setSubmitting] = useState(false);

  const filledCount = items.filter((it) => counts[it.id] !== '' && counts[it.id] != null).length;

  const setCount = (id, value) => {
    if (value === '' || value == null) {
      setCounts((c) => {
        const next = { ...c };
        delete next[id];
        return next;
      });
      return;
    }
    const n = Math.max(0, Math.floor(Number(value) || 0));
    setCounts((c) => ({ ...c, [id]: n }));
  };

  const canSubmit = Boolean(name.trim()) && filledCount > 0 && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {};
      for (const it of items) {
        if (counts[it.id] != null && counts[it.id] !== '') payload[it.id] = counts[it.id];
      }
      await api.submitAudit({ employee_name: name.trim(), counts: payload });
      setCounts({});
      setName('');
      pushToast('Audit submitted — live stock synced');
      refresh();
    } catch (err) {
      pushToast(err.message, 'ember');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeading
        eyebrow="Physical Count"
        title="Inventory Audit"
        live
        actions={
          <span className="font-mono text-[10px] uppercase tracking-widest text-dim">
            Decoupled from sales — absorbs shrink &amp; manager withdrawals
          </span>
        }
      />

      {loading && items.length === 0 ? (
        <div className="border border-edge bg-surface p-10 text-center font-mono text-xs uppercase tracking-widest text-dim">
          Loading inventory…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="max-w-3xl">
          <div className="space-y-3">
            {items.map((it) => {
              const value = counts[it.id];
              const hasValue = value != null && value !== '';
              return (
                <div
                  key={it.id}
                  className={`flex items-center justify-between gap-4 border bg-panel p-4 ${
                    hasValue ? 'border-neon/40' : 'border-edge'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm font-bold text-snow">{it.name}</p>
                    <p className="font-mono text-xs text-dim">
                      System stock{' '}
                      <span className={it.current_stock > 0 ? 'text-mist' : 'text-ember'}>
                        {it.current_stock}
                      </span>
                      <span className="mx-2 text-edge">|</span>
                      <span className="text-mist">enter actual count remaining</span>
                    </p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={counts[it.id] ?? ''}
                    placeholder={String(it.current_stock)}
                    onChange={(e) => setCount(it.id, e.target.value)}
                    className="tnum w-28 shrink-0 border border-edge bg-void px-3 py-2.5 text-right font-mono text-lg font-bold text-neon outline-none placeholder:font-normal placeholder:text-dim focus:border-neon focus:shadow-neon-teal"
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex flex-col gap-4 border border-edge bg-panel p-4 sm:flex-row sm:items-end">
            <Field label="Employee name" className="flex-1">
              <MonoInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. JOHNNY B."
                maxLength={40}
              />
            </Field>
            <Btn type="submit" variant="teal" size="lg" disabled={!canSubmit}>
              {submitting ? 'Submitting…' : `Submit Audit${filledCount > 0 ? ` (${filledCount})` : ''}`}
            </Btn>
          </div>
        </form>
      )}
    </div>
  );
}